import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GoogleMapReact from "google-map-react";
import { useSupercluster } from "./hooks/useSupercluster";
import Supercluster from "supercluster";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import LocationCapacityMarker from "./location-capacity-marker.component";
import {
  latitudeLocation,
  longitudeLocation,
  zoomLocation
} from "./functions/facility-report.functions";
import { processCoordinates } from "../functions/process-coordinates.function";
import { DarkMapStyles } from "./style/google-map.styles";
import * as _ from "lodash";
import {
  getFocusedLocation,
  handleMapApiLoaded
} from "./functions/facility-report-google-map.functions";

/** Exported for unit tests (map/reduce) and stable referential identity for `useSupercluster`. */
export const facilityReportSuperclusterOptions: Supercluster.Options<
  Supercluster.AnyProps,
  Supercluster.AnyProps
> = {
  radius: 75,
  maxZoom: 22,
  map: (props: Supercluster.AnyProps) => ({
    averageCapacity: props.averageCapacity,
    numberOfWorkplaces: props.numberOfWorkplaces,
    count: 1,
    locations: [props],
    name: props.name,
    id: props.id
  }),
  reduce: (acc: Supercluster.AnyProps, props: Supercluster.AnyProps) => {
    acc.locations = [...acc.locations, props];
    acc.count += 1;
    acc.numberOfWorkplaces += props.numberOfWorkplaces;
    acc.averageCapacity =
      (props.averageCapacity * props.numberOfWorkplaces) / acc.numberOfWorkplaces;
    return acc;
  }
};

type Props = {
  zoneBookingReport: boolean;
  focusedLocationId?: number | null;
  onMapReadyChange?: (isReady: boolean) => void;
};

/** Marker wrapper for `google-map-react` (exported for coverage). */
export const AnyReactComponent: React.FC<
  React.PropsWithChildren<{ lat?: number; lng?: number }>
> = ({ children }) => <div>{children}</div>;

const FacilityReportGoogleMap: React.FC<Props> = ({
  zoneBookingReport,
  focusedLocationId,
  onMapReadyChange
}) => {
  const {
    settings: { colorMode }
  } = useSelector((state: RootState) => state.login);
  const { mapMarkerLocations } = useSelector((state: RootState) => state.report);

  const [boundsState, setBoundsState] = useState<any>(null);
  const [zoomState, setZoomState] = useState(10);
  const [isMapApiLoaded, setIsMapApiLoaded] = useState(false);
  const mapRef = useRef<any>(null!);
  const theme = useTheme();

  const points: Supercluster.PointFeature<Supercluster.AnyProps>[] = useMemo(() => {
    const data = _.uniqBy(
      mapMarkerLocations ?? [],
      mapMarkerLocation => mapMarkerLocation.locationInventoryId
    );

    return data.map(location => processCoordinates(location));
  }, [mapMarkerLocations]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: boundsState,
    zoom: zoomState,
    options: facilityReportSuperclusterOptions
  });

  useEffect(() => {
    /* istanbul ignore next */
    // prevent pinch zoom of the mobile/tablet device
    function handleTouchMove(event: TouchEvent) {
      if (event.touches.length > 1) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
    globalThis.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => globalThis.removeEventListener("touchmove", handleTouchMove);
  }, []);

  /**
   * Dynamically adjusts the map view when more than one location is present.
   * `fitBounds` automatically zooms to make all locations visible.
   * A listener then ensures that the maximum zoom level of 13 is not exceeded
   * if the locations are very close to each other.
   */
  useEffect(() => {
    if (mapRef.current && mapMarkerLocations && mapMarkerLocations.length > 1) {
      // eslint-disable-next-line no-undef
      const bounds = new google.maps.LatLngBounds();
      mapMarkerLocations.forEach(loc => {
        const lat = loc.geoInformation?.[0]?.latitude;
        const lng = loc.geoInformation?.[0]?.longitude;
        if (lat && lng) {
          bounds.extend({ lat, lng });
        }
      });
      mapRef.current.fitBounds(bounds);

      // eslint-disable-next-line no-undef
      const listener = google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
        if (mapRef.current.getZoom() > 13) {
          mapRef.current.setZoom(13);
        }
      });
      return () => google.maps.event.removeListener(listener);
    }
  }, [mapMarkerLocations, mapRef.current]);

  /**
   * When a location is focused (`focusedLocationId`), pans and zooms the map to that marker
   * once the Google Maps instance and marker data are ready. Notifies `onMapReadyChange`:
   * `false` while prerequisites are missing, `true` after pan/zoom or when coords are absent.
   */
  useEffect(() => {
    getFocusedLocation({
      focusedLocationId,
      isMapApiLoaded,
      mapRef,
      mapMarkerLocations,
      onMapReadyChange
    });
  }, [isMapApiLoaded, mapMarkerLocations, focusedLocationId, onMapReadyChange]);

  /**
   * When no location is focused, reports overall map readiness: `false` until the Maps API
   * has loaded; then `true` once marker data has been fetched (`mapMarkerLocations` defined).
   */
  useEffect(() => {
    handleMapApiLoaded({
      focusedLocationId,
      isMapApiLoaded,
      mapMarkerLocations,
      onMapReadyChange
    });
  }, [isMapApiLoaded, mapMarkerLocations, focusedLocationId, onMapReadyChange]);

  useEffect(() => {
    return () => {
      onMapReadyChange?.(false);
    };
  }, []);

  return (
    <GoogleMapReact
      onGoogleApiLoaded={({ map }) => {
        mapRef.current = map;
        setIsMapApiLoaded(true);
      }}
      yesIWantToUseGoogleMapApiInternals
      options={colorMode === "dark" ? DarkMapStyles : { mapTypeControl: true, styles: [] }}
      bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "" }}
      center={{
        lat: latitudeLocation(mapMarkerLocations),
        lng: longitudeLocation(mapMarkerLocations)
      }}
      zoom={zoomLocation(mapMarkerLocations)}
      onChange={({ zoom, bounds }) => {
        setZoomState(zoom);
        setBoundsState([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
      }}
    >
      {clusters.map(cluster => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount, locations, id } = cluster.properties;

        if (isCluster) {
          let radius = 6 * locations ? locations.length : 0;
          if (radius < 30) radius = 30;
          return (
            <AnyReactComponent key={`cluster-${cluster.id}`} lat={latitude} lng={longitude}>
              {zoomState >= 18 ? (
                <div style={{ position: "relative", width: "24px", height: "24px" }}>
                  <LocationOnIcon />
                  {locations?.map((location: any, index: number) => {
                    const angle = (index * (2 * Math.PI)) / locations.length;
                    const x = Math.round(24 / 2 + radius * Math.cos(angle) - 30 / 2);
                    const y = Math.round(24 / 2 + radius * Math.sin(angle) - 30 / 2);
                    return (
                      <div
                        style={{ position: "absolute", left: `${x}px`, top: `${y}px` }}
                        key={location.id}
                      >
                        <LocationCapacityMarker
                          mode={zoneBookingReport}
                          id={location.id}
                          name={location.name}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Box
                  onClick={() => {
                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(cluster.id as number),
                      20
                    );
                    mapRef?.current?.setZoom(expansionZoom);
                    mapRef?.current?.panTo({ lat: latitude, lng: longitude });
                  }}
                  sx={{
                    width: `${10 + (pointCount / points.length) * 20}px`, // 30
                    height: `${10 + (pointCount / points.length) * 20}px`, // 30
                    background: theme.palette.primary.main, // 'white
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "2px solid white",
                    cursor: "pointer",
                    boxShadow: "10px 10px 20px -10px rgba(0,0,0,0.75)",
                    "&:hover": {
                      background: "#131421 !important"
                    }
                  }}
                >
                  <Typography style={{ fontWeight: "bold" }}> {pointCount}</Typography>
                </Box>
              )}
            </AnyReactComponent>
          );
        }

        return (
          <AnyReactComponent key={`crime-${cluster.properties.id}`} lat={latitude} lng={longitude}>
            <LocationCapacityMarker
              mode={zoneBookingReport}
              id={id}
              name={cluster.properties.name}
            />
          </AnyReactComponent>
        );
      })}
    </GoogleMapReact>
  );
};

export default FacilityReportGoogleMap;
